import { JourneyTimeline } from "@/page-components/dashboard/components/journey-timeline";
import { Badge } from "@/ui-components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui-components/ui/table";
import { useEffect, useState } from "react";

interface Lead {
  id: string;
  name: string;
  company: string;
  email: string;
  value: string;
  currentState: string;
  journey: string[];
}

interface LeadsTableProps {
  filter: string;
}

export function LeadsTable({ filter }: LeadsTableProps) {
  // Mock data - in a real app, this would come from an API
  const allLeads: Lead[] = [
    {
      id: "1",
      name: "John Smith",
      company: "Acme Inc",
      email: "john@acme.com",
      value: "$12,000",
      currentState: "thinking",
      journey: ["start", "wasnt-there", "thinking"],
    },
    {
      id: "2",
      name: "Lisa Johnson",
      company: "XYZ Corp",
      email: "lisa@xyz.com",
      value: "$24,500",
      currentState: "received-offer",
      journey: ["start", "thinking", "received-offer"],
    },
    {
      id: "3",
      name: "Michael Brown",
      company: "123 Industries",
      email: "michael@123industries.com",
      value: "$8,750",
      currentState: "thinking",
      journey: ["start", "thinking"],
    },
    {
      id: "4",
      name: "Sarah Davis",
      company: "Tech Solutions",
      email: "sarah@techsolutions.com",
      value: "$32,000",
      currentState: "sold",
      journey: ["start", "thinking", "received-offer", "sold"],
    },
    {
      id: "5",
      name: "Robert Wilson",
      company: "Global Services",
      email: "robert@globalservices.com",
      value: "$18,500",
      currentState: "didnt-sell",
      journey: ["start", "thinking", "received-offer", "didnt-sell"],
    },
    {
      id: "6",
      name: "Jennifer Taylor",
      company: "Innovative Systems",
      email: "jennifer@innovative.com",
      value: "$15,200",
      currentState: "no-interest",
      journey: ["start", "no-interest"],
    },
    {
      id: "7",
      name: "David Martinez",
      company: "Premier Products",
      email: "david@premier.com",
      value: "$22,800",
      currentState: "technical-not-possible",
      journey: ["start", "thinking", "received-offer", "technical-not-possible"],
    },
  ];

  const [filteredLeads, setFilteredLeads] = useState<Lead[]>(allLeads);

  useEffect(() => {
    if (filter === "all") {
      setFilteredLeads(allLeads);
    } else {
      setFilteredLeads(allLeads.filter((lead) => lead.currentState === filter));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "start":
        return <Badge variant="outline">Start</Badge>;
      case "no-interest":
      case "didnt-sell":
      case "technical-not-possible":
        return <Badge variant="destructive">Verloren</Badge>;
      case "wasnt-there":
      case "thinking":
      case "received-offer":
        return <Badge variant="secondary">Ausstehend</Badge>;
      case "sold":
        return (
          <Badge variant="default" className="bg-green-600">
            Gewonnen
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[180px] md:w-[220px]">Name</TableHead>
            <TableHead className="hidden md:table-cell">Unternehmen</TableHead>
            <TableHead className="w-[80px]">Wert</TableHead>
            <TableHead className="w-[100px]">Status</TableHead>
            <TableHead className="w-[50%]">Benotung</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredLeads.map((lead) => (
            <TableRow key={lead.id}>
              <TableCell className="font-medium">
                <div>
                  {lead.name}
                  <div className="text-xs text-muted-foreground">{lead.email}</div>
                </div>
              </TableCell>
              <TableCell className="hidden md:table-cell">{lead.company}</TableCell>
              <TableCell>{lead.value}</TableCell>
              <TableCell>{getStatusBadge(lead.currentState)}</TableCell>
              <TableCell className="w-[50%]">
                <JourneyTimeline journey={lead.journey} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
